<template>
  <div class="kana-study">
    <div v-if="loading" class="loading">Loading...</div>
    
    <!-- Study Session -->
    <div v-else-if="studyMode && currentIndex < studyItems.length" class="study-session">
      <!-- Progress bar -->
      <div class="progress-header">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="progress-text">{{ currentIndex + 1 }} / {{ studyItems.length }}</div>
      </div>

      <!-- Current kana -->
      <div class="kana-card">
        <div class="kana-character">{{ currentItem.character }}</div>
        <div class="kana-type">{{ currentItem.type }}</div>
        <div v-if="currentItem.status === 'review'" class="status-badge review">Review</div>
        <div v-else class="status-badge new">New</div>
      </div>

      <!-- Input area -->
      <div class="input-area">
        <input 
          v-model="userAnswer" 
          @keyup.enter="submitAnswer"
          :disabled="showingFeedback"
          placeholder="Type romaji..."
          ref="answerInput"
          class="answer-input"
        />
        <button @click="submitAnswer" :disabled="!userAnswer || showingFeedback" class="submit-btn">
          Submit
        </button>
      </div>

      <!-- Feedback -->
      <transition name="fade">
        <div v-if="showingFeedback" class="feedback" :class="feedbackClass">
          <div class="feedback-icon">{{ isCorrect ? '✓' : '✗' }}</div>
          <div class="feedback-text">
            {{ isCorrect ? 'Correct!' : `Incorrect. The answer is: ${currentItem.romaji}` }}
          </div>
          <div v-if="!isCorrect" class="correct-answer">{{ currentItem.romaji }}</div>
        </div>
      </transition>

      <!-- SRS Info -->
      <div v-if="currentProgress" class="srs-info">
        <div class="srs-level">Level: {{ getSRSStage(currentProgress.srsLevel) }}</div>
        <div class="srs-streak">Streak: {{ currentProgress.currentStreak || 0 }}</div>
        <div class="srs-accuracy">
          Accuracy: {{ calculateAccuracy(currentProgress) }}%
        </div>
      </div>
    </div>

    <!-- Session Complete -->
    <div v-else-if="studyMode && currentIndex >= studyItems.length" class="session-complete">
      <h2>Session Complete! 🎉</h2>
      <div class="stats-summary">
        <div class="stat-item">
          <div class="stat-label">Correct</div>
          <div class="stat-value correct">{{ sessionCorrect }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Incorrect</div>
          <div class="stat-value incorrect">{{ sessionIncorrect }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Accuracy</div>
          <div class="stat-value">{{ sessionAccuracy }}%</div>
        </div>
      </div>
      <button @click="loadStudySet" class="action-btn">Start New Session</button>
      <button @click="studyMode = false" class="action-btn secondary">Back to Overview</button>
    </div>

    <!-- Overview/Start Screen -->
    <div v-else class="overview">
      <h1>Kana Study</h1>
      
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-number">{{ reviewCount }}</div>
          <div class="stat-label">Due for Review</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ totalLearned }}</div>
          <div class="stat-label">Total Learned</div>
        </div>
      </div>

      <button @click="startStudy" class="start-btn" :disabled="!canStudy">
        {{ canStudy ? 'Start Study Session' : 'No items to study' }}
      </button>
      
      <button @click="showAllKana = !showAllKana" class="toggle-btn">
        {{ showAllKana ? 'Hide' : 'Show' }} All Kana
      </button>

      <!-- All Kana Chart -->
      <div v-if="showAllKana" class="kana-chart">
        <h3>Kana Chart</h3>
        <div class="chart-grid">
          <div v-for="kana in allKana" :key="kana.character" class="kana-item" :class="getKanaClass(kana)">
            <div class="kana-char">{{ kana.character }}</div>
            <div class="kana-romaji">{{ kana.romaji }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import axios from 'axios';
import confetti from 'canvas-confetti';

const loading = ref(true);
const studyMode = ref(false);
const studyItems = ref([]);
const currentIndex = ref(0);
const userAnswer = ref('');
const showingFeedback = ref(false);
const isCorrect = ref(false);
const feedbackClass = ref('');
const answerInput = ref(null);
const sessionCorrect = ref(0);
const sessionIncorrect = ref(0);
const showAllKana = ref(false);
const allKana = ref([]);
const userProgress = ref({});

const token = localStorage.getItem('token');

const currentItem = computed(() => studyItems.value[currentIndex.value] || {});
const currentProgress = computed(() => userProgress.value[currentItem.value.character] || null);
const progressPercent = computed(() => (currentIndex.value / studyItems.value.length) * 100);
const sessionTotal = computed(() => sessionCorrect.value + sessionIncorrect.value);
const sessionAccuracy = computed(() => 
  sessionTotal.value > 0 ? Math.round((sessionCorrect.value / sessionTotal.value) * 100) : 0
);
const reviewCount = computed(() => studyItems.value.filter(i => i.status === 'review').length);
const totalLearned = computed(() => Object.keys(userProgress.value).length);
const canStudy = computed(() => studyItems.value.length > 0);

const SRS_STAGES = {
  0: 'New',
  1: 'Apprentice I',
  2: 'Apprentice II', 
  3: 'Apprentice III',
  4: 'Apprentice IV',
  5: 'Guru I',
  6: 'Guru II',
  7: 'Master',
  8: 'Enlightened',
  9: 'Burned'
};

onMounted(async () => {
  await loadProgress();
  await loadAllKana();
  await loadStudySet();
  loading.value = false;
});

async function loadProgress() {
  if (!token) return;
  try {
    const res = await axios.get('/api/progress', {
      headers: { 'x-auth-token': token }
    });
    userProgress.value = res.data.data.kanaProgress || {};
  } catch (err) {
    console.error('Failed to load progress:', err);
  }
}

async function loadAllKana() {
  try {
    const res = await axios.get('/api/kana/all');
    allKana.value = res.data.data || [];
  } catch (err) {
    console.error('Failed to load kana list:', err);
  }
}

async function loadStudySet() {
  if (!token) return;
  try {
    const res = await axios.get('/api/kana/study', {
      headers: { 'x-auth-token': token }
    });
    studyItems.value = res.data.data || [];
    currentIndex.value = 0;
    sessionCorrect.value = 0;
    sessionIncorrect.value = 0;
  } catch (err) {
    console.error('Failed to load study set:', err);
  }
}

function startStudy() {
  if (studyItems.value.length === 0) return;
  studyMode.value = true;
  nextTick(() => {
    answerInput.value?.focus();
  });
}

async function submitAnswer() {
  if (!userAnswer.value || showingFeedback.value) return;
  
  const correct = userAnswer.value.trim().toLowerCase() === currentItem.value.romaji.toLowerCase();
  isCorrect.value = correct;
  showingFeedback.value = true;
  feedbackClass.value = correct ? 'correct' : 'incorrect';

  if (correct) {
    sessionCorrect.value++;
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.7 }
    });
  } else {
    sessionIncorrect.value++;
  }

  // Update progress on server
  try {
    const res = await axios.post('/api/kana/review', 
      {
        character: currentItem.value.character,
        correct
      },
      { headers: { 'x-auth-token': token } }
    );
    
    // Update local progress
    if (res.data.data) {
      userProgress.value[currentItem.value.character] = res.data.data;
    }
  } catch (err) {
    console.error('Failed to update progress:', err);
  }

  // Move to next after delay
  setTimeout(() => {
    showingFeedback.value = false;
    userAnswer.value = '';
    currentIndex.value++;
    
    if (currentIndex.value < studyItems.value.length) {
      nextTick(() => {
        answerInput.value?.focus();
      });
    }
  }, 1500);
}

function getSRSStage(level) {
  return SRS_STAGES[level] || 'Unknown';
}

function calculateAccuracy(progress) {
  const total = (progress.correctCount || 0) + (progress.incorrectCount || 0);
  if (total === 0) return 0;
  return Math.round(((progress.correctCount || 0) / total) * 100);
}

function getKanaClass(kana) {
  const progress = userProgress.value[kana.character];
  if (!progress) return 'not-learned';
  
  const level = progress.srsLevel || 0;
  if (level === 0) return 'not-learned';
  if (level <= 4) return 'apprentice';
  if (level <= 6) return 'guru';
  if (level === 7) return 'master';
  if (level === 8) return 'enlightened';
  return 'burned';
}
</script>

<style scoped>
.kana-study {
  max-width: 600px;
  margin: 2rem auto;
  padding: 1rem;
}

.loading {
  text-align: center;
  font-size: 1.5rem;
  padding: 3rem;
}

/* Study Session */
.study-session {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.progress-header {
  margin-bottom: 2rem;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4f8cff, #3a75e6);
  transition: width 0.3s ease;
}

.progress-text {
  text-align: right;
  font-size: 0.9rem;
  color: #666;
}

.kana-card {
  text-align: center;
  padding: 2rem;
  position: relative;
}

.kana-character {
  font-size: 8rem;
  font-weight: bold;
  color: #2c3e50;
  margin: 1rem 0;
}

.kana-type {
  font-size: 1.2rem;
  color: #666;
  text-transform: capitalize;
}

.status-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.status-badge.review {
  background: #fff3cd;
  color: #856404;
}

.status-badge.new {
  background: #d1ecf1;
  color: #0c5460;
}

.input-area {
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
}

.answer-input {
  flex: 1;
  padding: 1rem;
  font-size: 1.2rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  outline: none;
}

.answer-input:focus {
  border-color: #4f8cff;
}

.submit-btn {
  padding: 1rem 2rem;
  background: #4f8cff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #3a75e6;
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.feedback {
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1rem 0;
}

.feedback.correct {
  background: #d4edda;
  color: #155724;
}

.feedback.incorrect {
  background: #f8d7da;
  color: #721c24;
}

.feedback-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.feedback-text {
  font-size: 1.2rem;
  font-weight: 600;
}

.correct-answer {
  font-size: 1.5rem;
  margin-top: 0.5rem;
  font-weight: bold;
}

.srs-info {
  display: flex;
  justify-content: space-around;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #666;
}

/* Session Complete */
.session-complete {
  text-align: center;
  padding: 3rem 2rem;
}

.session-complete h2 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
}

.stats-summary {
  display: flex;
  justify-content: space-around;
  margin: 2rem 0;
}

.stat-item {
  text-align: center;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50;
}

.stat-value.correct {
  color: #28a745;
}

.stat-value.incorrect {
  color: #dc3545;
}

.action-btn {
  margin: 0.5rem;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  background: #4f8cff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.action-btn:hover {
  background: #3a75e6;
}

.action-btn.secondary {
  background: #6c757d;
}

.action-btn.secondary:hover {
  background: #5a6268;
}

/* Overview */
.overview {
  text-align: center;
}

.overview h1 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stat-number {
  font-size: 3rem;
  font-weight: bold;
  color: #4f8cff;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
}

.start-btn {
  margin: 2rem 0;
  padding: 1.5rem 3rem;
  font-size: 1.3rem;
  background: #4f8cff;
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(79, 140, 255, 0.3);
  transition: all 0.2s;
}

.start-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(79, 140, 255, 0.4);
}

.start-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.toggle-btn {
  margin: 1rem;
  padding: 0.75rem 1.5rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.toggle-btn:hover {
  background: #5a6268;
}

/* Kana Chart */
.kana-chart {
  margin-top: 2rem;
  text-align: left;
}

.kana-chart h3 {
  margin-bottom: 1rem;
  color: #2c3e50;
}

.chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.5rem;
}

.kana-item {
  padding: 1rem;
  text-align: center;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  transition: all 0.2s;
}

.kana-item.not-learned {
  background: #f8f9fa;
  border-color: #e0e0e0;
}

.kana-item.apprentice {
  background: #fff3cd;
  border-color: #ffc107;
}

.kana-item.guru {
  background: #d1ecf1;
  border-color: #17a2b8;
}

.kana-item.master {
  background: #d4edda;
  border-color: #28a745;
}

.kana-item.enlightened {
  background: #e2d9f3;
  border-color: #6f42c1;
}

.kana-item.burned {
  background: #f8d7da;
  border-color: #dc3545;
}

.kana-char {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
}

.kana-romaji {
  font-size: 0.8rem;
  color: #666;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .kana-character {
    font-size: 5rem;
  }
  
  .stats-summary {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
